import { Alert, Button, Checkbox, Description, Label, Radio, RadioGroup, Surface } from "@heroui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ConfiguredProductInput, PublicOptionGroup, PublicProduct, SelectedOptionInput } from "../../../../shared/types";
import { hasActiveOptionGroups, hasSelectableIngredients } from "../../../../shared/product-configurability";
import { MAX_CART_LINES, configurationIssue, configuredUnitPrice, useCart } from "../../../contexts/CartContext";
import type { CartLine, CartProductConfiguration } from "../../../contexts/CartContext";
import { useTenant } from "../../../contexts/TenantContext";
import { useToast } from "../../../contexts/ToastContext";
import { formatLocal, formatUsd } from "../../../lib/currency";
import { availableProduct, cloneConfiguration, createCartConfiguration, createProductConfiguration, effectiveBasePrice, needsSteps, selectionRule } from "../configuration";

import { ConfigurationAlert, MenuSelect, QuantityField, ResponsiveDrawer } from "./MenuControls";
import { configuredProductSummary } from "./configurationSummary";

interface ProductStep {
  id: string;
  label: string;
  type:
    | "variant"
    | "option-group"
    | "ingredients"
    | "combo-component"
    | "review";
  groupId?: string;
  componentId?: string;
}

function useProductSteps(
  product: PublicProduct | null,
  configuration: CartProductConfiguration | null,
): ProductStep[] {
  return useMemo(() => {
    if (!product || !configuration) return [];
    const result: ProductStep[] = [];
    if (product.priceMode === "variante") {
      result.push({ id: "variant", label: "Presentación", type: "variant" });
    }
    if (product.type === "combo") {
      for (const component of product.comboComponents.filter(
        (item) => item.quantity > 0 && item.products.length > 0,
      )) {
        result.push({
          id: `combo-${component.id}`,
          label: component.name,
          type: "combo-component",
          componentId: component.id,
        });
      }
    } else {
      const applicableGroups = product.optionGroups.filter(
        (group) =>
          group.variantIds.length === 0 ||
          (configuration.variantId
            ? group.variantIds.includes(configuration.variantId)
            : false),
      );
      for (const group of applicableGroups.filter((item) =>
        hasActiveOptionGroups({ optionGroups: [item] }),
      )) {
        result.push({
          id: `group-${group.id}`,
          label: group.name,
          type: "option-group",
          groupId: group.id,
        });
      }
      if (hasSelectableIngredients(product)) {
        result.push({
          id: "ingredients",
          label: "Ingredientes",
          type: "ingredients",
        });
      }
    }
    result.push({ id: "review", label: "Revisar", type: "review" });
    return result;
  }, [product, configuration?.variantId]);
}

function ProductStepper({
  steps,
  currentStepIndex,
}: {
  steps: ProductStep[];
  currentStepIndex: number;
}) {
  return (
    <nav aria-label="Pasos de configuración" className="overflow-x-auto">
      <ol className="flex items-center gap-1 px-1 py-2">
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;
          return (
            <li key={step.id} className="flex items-center gap-1">
              {index > 0 && (
                <div
                  className={`h-px w-4 sm:w-8 ${isCompleted || isCurrent ? "bg-[var(--menu-accent)]" : "bg-border"}`}
                />
              )}
              <div className="flex items-center gap-2">
                <span
                  className={`grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold ${
                    isCompleted
                      ? "bg-[var(--menu-accent)] text-[var(--menu-accent-foreground)]"
                      : isCurrent
                        ? "border-2 border-[var(--menu-accent)] text-[var(--menu-accent)]"
                        : "border border-border text-muted"
                  }`}
                >
                  {isCompleted ? "✓" : index + 1}
                </span>
                <span
                  className={`hidden text-xs font-semibold sm:inline ${isCurrent ? "menu-text-primary" : "menu-text-muted"}`}
                >
                  {step.label}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function isIngredientSelected(
  configuration: Pick<
    ConfiguredProductInput,
    "excludedIngredientIds" | "includedIngredientIds"
  >,
  ingredientId: string,
  includedByDefault: boolean,
): boolean {
  return includedByDefault
    ? !configuration.excludedIngredientIds.includes(ingredientId)
    : configuration.includedIngredientIds.includes(ingredientId);
}

export function withIngredientSelection(
  configuration: ConfiguredProductInput,
  ingredient: { id: string; includedByDefault: boolean; selectable: boolean },
  selected: boolean,
): ConfiguredProductInput {
  if (!ingredient.selectable) return configuration;
  return {
    ...configuration,
    excludedIngredientIds: ingredient.includedByDefault
      ? selected
        ? configuration.excludedIngredientIds.filter(
            (id) => id !== ingredient.id,
          )
        : [...new Set([...configuration.excludedIngredientIds, ingredient.id])]
      : configuration.excludedIngredientIds,
    includedIngredientIds: ingredient.includedByDefault
      ? configuration.includedIngredientIds
      : selected
        ? [...new Set([...configuration.includedIngredientIds, ingredient.id])]
        : configuration.includedIngredientIds.filter(
            (id) => id !== ingredient.id,
          ),
  };
}

function VariantStep({
  product,
  branchId,
  configuration,
  onChange,
}: {
  product: PublicProduct;
  branchId: string;
  configuration: CartProductConfiguration;
  onChange(value: ConfiguredProductInput): void;
}) {
  return (
    <MenuSelect
      isRequired
      items={product.variants.map((variant) => ({
        id: variant.id,
        label: variant.name,
        description: formatUsd(
          effectiveBasePrice(product, variant.priceUsdCents),
        ),
        disabled: Boolean(
          branchId && variant.unavailableBranchIds.includes(branchId),
        ),
      }))}
      label="Presentación"
      placeholder="Elige una presentación"
      value={configuration.variantId ?? ""}
      onChange={(variantId) =>
        onChange({ ...configuration, variantId: variantId || null, groups: [] })
      }
    />
  );
}

function OptionGroupStep({
  group,
  branchId,
  configuration,
  onChange,
}: {
  group: PublicOptionGroup;
  branchId: string;
  configuration: ConfiguredProductInput;
  onChange(value: ConfiguredProductInput): void;
}) {
  const selections =
    configuration.groups.find((entry) => entry.groupId === group.id)?.options ??
    [];
  const selectionCount = selections.reduce(
    (sum, selection) =>
      sum + (group.selectionType === "quantity" ? selection.quantity : 1),
    0,
  );
  const help = `${selectionRule(group)} Seleccionado: ${selectionCount}.`;
  const setGroupSelections = (options: SelectedOptionInput[]) => {
    const otherGroups = configuration.groups.filter(
      (g) => g.groupId !== group.id,
    );
    onChange({
      ...configuration,
      groups: options.length
        ? [...otherGroups, { groupId: group.id, options }]
        : otherGroups,
    });
  };

  if (group.selectionType === "single") {
    const noneValue = `none-${group.id}`;
    const minimum = Math.max(group.required ? 1 : 0, group.minSelections);
    return (
      <Surface
        className="menu-panel-alt rounded-[var(--menu-radius)] border border-border p-4"
        variant="secondary"
      >
        <RadioGroup
          value={selections[0]?.optionId ?? noneValue}
          variant="secondary"
          onChange={(optionId: string) =>
            setGroupSelections(
              optionId === noneValue ? [] : [{ optionId, quantity: 1 }],
            )
          }
        >
          <Label>{group.name}</Label>
          <Description>{group.description || help}</Description>
          {minimum === 0 && (
            <Radio value={noneValue}>
              <Radio.Content>
                <Radio.Control>
                  <Radio.Indicator />
                </Radio.Control>
                <span className="menu-text-primary font-medium">Ninguna</span>
              </Radio.Content>
            </Radio>
          )}
          {group.options.map((option) => (
            <Radio
              key={option.id}
              isDisabled={Boolean(
                branchId && option.unavailableBranchIds.includes(branchId),
              )}
              value={option.id}
            >
              <Radio.Content>
                <Radio.Control>
                  <Radio.Indicator />
                </Radio.Control>
                <span className="flex flex-1 items-center justify-between gap-3">
                  <span>{option.name}</span>
                  {option.extraPriceUsdCents > 0 && (
                    <span className="menu-price text-xs font-bold">
                      +{formatUsd(option.extraPriceUsdCents)}
                    </span>
                  )}
                </span>
              </Radio.Content>
            </Radio>
          ))}
          <Description>{help}</Description>
        </RadioGroup>
      </Surface>
    );
  }

  return (
    <Surface
      className="menu-panel-alt rounded-[var(--menu-radius)] border border-border p-4"
      variant="secondary"
    >
      <div className="mb-3">
        <h4 className="menu-text-primary font-bold">{group.name}</h4>
        <p className="menu-text-muted text-xs leading-5">
          {group.description || help}
        </p>
        {group.description && (
          <p className="menu-text-muted text-xs font-semibold">{help}</p>
        )}
      </div>
      <div className="grid gap-2">
        {group.options.map((option) => {
          const selection = selections.find(
            (entry) => entry.optionId === option.id,
          );
          const selected = Boolean(selection);
          const unavailable = Boolean(
            branchId && option.unavailableBranchIds.includes(branchId),
          );
          return (
            <div
              key={option.id}
              className="menu-panel flex flex-wrap items-center justify-between gap-3 rounded-[var(--menu-radius)] border border-border p-3"
            >
              <Checkbox
                isDisabled={
                  unavailable ||
                  (!selected && selectionCount >= group.maxSelections)
                }
                isSelected={selected}
                variant="secondary"
                onChange={(checked: boolean) =>
                  setGroupSelections(
                    checked
                      ? [...selections, { optionId: option.id, quantity: 1 }]
                      : selections.filter(
                          (entry) => entry.optionId !== option.id,
                        ),
                  )
                }
              >
                <Checkbox.Content>
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                  <span className="menu-text-primary font-medium">
                    {option.name}
                  </span>
                </Checkbox.Content>
              </Checkbox>
              <div className="flex items-center gap-3">
                {option.extraPriceUsdCents > 0 && (
                  <span className="menu-price text-xs font-bold">
                    +{formatUsd(option.extraPriceUsdCents)}
                  </span>
                )}
                {group.selectionType === "quantity" && selected && (
                  <QuantityField
                    compact
                    label={`Cantidad de ${option.name}`}
                    max={Math.max(
                      1,
                      group.maxSelections -
                        (selectionCount - (selection?.quantity ?? 1)),
                    )}
                    value={selection?.quantity ?? 1}
                    onChange={(quantity) =>
                      setGroupSelections(
                        selections.map((entry) =>
                          entry.optionId === option.id
                            ? { ...entry, quantity }
                            : entry,
                        ),
                      )
                    }
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Surface>
  );
}

function IngredientsStep({
  product,
  configuration,
  onChange,
}: {
  product: PublicProduct;
  configuration: ConfiguredProductInput;
  onChange(value: ConfiguredProductInput): void;
}) {
  const setIngredient = (
    ingredient: PublicProduct["ingredients"][number],
    selected: boolean,
  ) => {
    onChange(withIngredientSelection(configuration, ingredient, selected));
  };

  return (
    <section className="grid gap-3">
      <h4 className="menu-text-primary font-bold">Ingredientes</h4>
      <p className="menu-text-muted text-xs leading-5">
        Ajusta la preparación a tu gusto.
      </p>
      <div className="divide-y divide-border/70">
        {product.ingredients.map((ingredient) =>
          ingredient.selectable ? (
            <Checkbox
              key={ingredient.id}
              className="min-h-12 py-2"
              isSelected={isIngredientSelected(
                configuration,
                ingredient.id,
                ingredient.includedByDefault,
              )}
              variant="secondary"
              onChange={(selected: boolean) =>
                setIngredient(ingredient, selected)
              }
            >
              <Checkbox.Content>
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <span className="menu-text-primary truncate font-medium">
                    {ingredient.name}
                  </span>
                  {ingredient.extraPriceUsdCents > 0 && (
                    <span className="menu-price shrink-0 text-xs font-bold">
                      +{formatUsd(ingredient.extraPriceUsdCents)}
                    </span>
                  )}
                </span>
              </Checkbox.Content>
            </Checkbox>
          ) : (
            <div
              key={ingredient.id}
              className="flex min-h-12 items-center justify-between gap-3 py-2 text-sm"
            >
              <span className="menu-text-secondary min-w-0 truncate font-medium">
                {ingredient.name}
              </span>
              <span className="menu-text-muted shrink-0 text-xs">Incluido</span>
            </div>
          ),
        )}
      </div>
    </section>
  );
}

function ComboComponentStep({
  component,
  branchId,
  products,
  configuration,
  onChange,
}: {
  component: PublicProduct["comboComponents"][number];
  branchId: string;
  products: ReadonlyMap<string, PublicProduct>;
  configuration: CartProductConfiguration;
  onChange(value: CartProductConfiguration): void;
}) {
  const configured = configuration.comboComponents.find(
    (entry) => entry.componentId === component.id,
  );
  const units = configured?.units ?? [];
  const choices = component.products.map((choice) => {
    const choiceProduct = products.get(choice.productId);
    return {
      id: choice.productId,
      label: choiceProduct?.name ?? "Producto no disponible",
      description:
        choice.extraPriceUsdCents > 0
          ? `+${formatUsd(choice.extraPriceUsdCents)}`
          : "Incluido",
      disabled: !availableProduct(choiceProduct, branchId),
    };
  });
  const setUnits = (nextUnits: ConfiguredProductInput[]) => {
    onChange({
      ...configuration,
      comboComponents: configuration.comboComponents.map((c) =>
        c.componentId === component.id ? { ...c, units: nextUnits } : c,
      ),
    });
  };

  return (
    <Surface
      className="menu-panel-alt rounded-[var(--menu-radius)] border border-border p-4"
      variant="secondary"
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h3 className="menu-text-primary font-black">{component.name}</h3>
          <p className="menu-text-muted text-xs">
            Configura {component.quantity}{" "}
            {component.quantity === 1 ? "unidad" : "unidades"}.
          </p>
        </div>
        <span className="rounded-full bg-[var(--menu-accent)] px-3 py-1 text-xs font-bold text-[var(--menu-accent-foreground)]">
          {component.quantity}x
        </span>
      </div>
      <div className="grid gap-4">
        {Array.from({ length: component.quantity }, (_, index) => {
          const unit = units[index];
          const unitProduct = unit ? products.get(unit.productId) : undefined;
          return (
            <Surface
              key={`${component.id}-${index}`}
              className="menu-panel rounded-[var(--menu-radius)] border border-border p-4"
              variant="default"
            >
              <MenuSelect
                isDisabled={
                  component.selectionType === "fijo" &&
                  choices.filter((choice) => !choice.disabled).length === 1
                }
                isRequired
                items={choices}
                label={`${component.name} ${index + 1}`}
                placeholder="Elige un producto"
                value={unit?.productId ?? ""}
                onChange={(productId) => {
                  const nextProduct = products.get(productId);
                  if (!nextProduct) return;
                  const nextUnits = [...units];
                  nextUnits[index] = createProductConfiguration(
                    nextProduct,
                    branchId,
                  );
                  setUnits(nextUnits);
                }}
              />
              {unit && unitProduct && (
                <div className="mt-4 border-t border-border pt-4">
                  <ProductConfigurationFields
                    compact
                    branchId={branchId}
                    product={unitProduct}
                    value={unit}
                    onChange={(nextUnit) => {
                      const nextUnits = [...units];
                      nextUnits[index] = nextUnit;
                      setUnits(nextUnits);
                    }}
                  />
                </div>
              )}
            </Surface>
          );
        })}
      </div>
    </Surface>
  );
}

function ReviewStep({
  product,
  configuration,
}: {
  product: PublicProduct;
  configuration: CartProductConfiguration;
}) {
  const details = configuredProductSummary(product, configuration);
  return (
    <section className="grid gap-2 border-t border-border pt-4">
      <h4 className="menu-text-primary mb-2 font-bold">Tu selección</h4>
      {details.length > 0 ? (
        <ul className="grid gap-1">
          {details.map((detail) => (
            <li key={detail} className="menu-text-secondary text-sm">
              · {detail}
            </li>
          ))}
        </ul>
      ) : (
        <p className="menu-text-muted text-sm">
          Sin personalizaciones adicionales.
        </p>
      )}
    </section>
  );
}

function ProductConfigurationFields({
  product,
  value,
  onChange,
  branchId,
  compact = false,
}: {
  product: PublicProduct;
  value: ConfiguredProductInput;
  onChange(value: ConfiguredProductInput): void;
  branchId: string;
  compact?: boolean;
}) {
  const applicableGroups = product.optionGroups.filter(
    (group) =>
      group.variantIds.length === 0 ||
      (value.variantId ? group.variantIds.includes(value.variantId) : false),
  );

  const setGroupSelections = (
    groupId: string,
    options: SelectedOptionInput[],
  ) => {
    const otherGroups = value.groups.filter(
      (group) => group.groupId !== groupId,
    );
    onChange({
      ...value,
      groups: options.length
        ? [...otherGroups, { groupId, options }]
        : otherGroups,
    });
  };

  const setIngredient = (
    ingredient: PublicProduct["ingredients"][number],
    selected: boolean,
  ) => {
    onChange(withIngredientSelection(value, ingredient, selected));
  };

  return (
    <div className={`menu-density-section grid ${compact ? "gap-4" : "gap-6"}`}>
      {product.priceMode === "variante" && (
        <MenuSelect
          isRequired
          items={product.variants.map((variant) => ({
            id: variant.id,
            label: variant.name,
            description: formatUsd(
              effectiveBasePrice(product, variant.priceUsdCents),
            ),
            disabled: Boolean(
              branchId && variant.unavailableBranchIds.includes(branchId),
            ),
          }))}
          label="Presentación"
          placeholder="Elige una presentación"
          value={value.variantId ?? ""}
          onChange={(variantId) =>
            onChange({ ...value, variantId: variantId || null, groups: [] })
          }
        />
      )}

      {applicableGroups.map((group) => {
        const selections =
          value.groups.find((entry) => entry.groupId === group.id)?.options ??
          [];
        const selectionCount = selections.reduce(
          (sum, selection) =>
            sum + (group.selectionType === "quantity" ? selection.quantity : 1),
          0,
        );
        const help = `${selectionRule(group)} Seleccionado: ${selectionCount}.`;

        if (group.selectionType === "single") {
          const noneValue = `none-${group.id}`;
          const minimum = Math.max(group.required ? 1 : 0, group.minSelections);
          return (
            <Surface
              key={group.assignmentId}
              className="menu-panel-alt rounded-[var(--menu-radius)] border border-border p-4"
              variant="secondary"
            >
              <RadioGroup
                value={selections[0]?.optionId ?? noneValue}
                variant="secondary"
                onChange={(optionId: string) =>
                  setGroupSelections(
                    group.id,
                    optionId === noneValue ? [] : [{ optionId, quantity: 1 }],
                  )
                }
              >
                <Label>{group.name}</Label>
                <Description>{group.description || help}</Description>
                {minimum === 0 && (
                  <Radio value={noneValue}>
                    <Radio.Content>
                      <Radio.Control>
                        <Radio.Indicator />
                      </Radio.Control>
                      <span className="menu-text-primary font-medium">
                        Ninguna
                      </span>
                    </Radio.Content>
                  </Radio>
                )}
                {group.options.map((option) => (
                  <Radio
                    key={option.id}
                    isDisabled={Boolean(
                      branchId &&
                      option.unavailableBranchIds.includes(branchId),
                    )}
                    value={option.id}
                  >
                    <Radio.Content>
                      <Radio.Control>
                        <Radio.Indicator />
                      </Radio.Control>
                      <span className="flex flex-1 items-center justify-between gap-3">
                        <span>{option.name}</span>
                        {option.extraPriceUsdCents > 0 && (
                          <span className="menu-price text-xs font-bold">
                            +{formatUsd(option.extraPriceUsdCents)}
                          </span>
                        )}
                      </span>
                    </Radio.Content>
                  </Radio>
                ))}
                <Description>{help}</Description>
              </RadioGroup>
            </Surface>
          );
        }

        return (
          <Surface
            key={group.assignmentId}
            className="menu-panel-alt rounded-[var(--menu-radius)] border border-border p-4"
            variant="secondary"
          >
            <div className="mb-3">
              <h4 className="menu-text-primary font-bold">{group.name}</h4>
              <p className="menu-text-muted text-xs leading-5">
                {group.description || help}
              </p>
              {group.description && (
                <p className="menu-text-muted text-xs font-semibold">{help}</p>
              )}
            </div>
            <div className="grid gap-2">
              {group.options.map((option) => {
                const selection = selections.find(
                  (entry) => entry.optionId === option.id,
                );
                const selected = Boolean(selection);
                const unavailable = Boolean(
                  branchId && option.unavailableBranchIds.includes(branchId),
                );
                return (
                  <div
                    key={option.id}
                    className="menu-panel flex flex-wrap items-center justify-between gap-3 rounded-[var(--menu-radius)] border border-border p-3"
                  >
                    <Checkbox
                      isDisabled={
                        unavailable ||
                        (!selected && selectionCount >= group.maxSelections)
                      }
                      isSelected={selected}
                      variant="secondary"
                      onChange={(checked: boolean) =>
                        setGroupSelections(
                          group.id,
                          checked
                            ? [
                                ...selections,
                                { optionId: option.id, quantity: 1 },
                              ]
                            : selections.filter(
                                (entry) => entry.optionId !== option.id,
                              ),
                        )
                      }
                    >
                      <Checkbox.Content>
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <span className="menu-text-primary font-medium">
                          {option.name}
                        </span>
                      </Checkbox.Content>
                    </Checkbox>
                    <div className="flex items-center gap-3">
                      {option.extraPriceUsdCents > 0 && (
                        <span className="menu-price text-xs font-bold">
                          +{formatUsd(option.extraPriceUsdCents)}
                        </span>
                      )}
                      {group.selectionType === "quantity" && selected && (
                        <QuantityField
                          compact
                          label={`Cantidad de ${option.name}`}
                          max={Math.max(
                            1,
                            group.maxSelections -
                              (selectionCount - (selection?.quantity ?? 1)),
                          )}
                          value={selection?.quantity ?? 1}
                          onChange={(quantity) =>
                            setGroupSelections(
                              group.id,
                              selections.map((entry) =>
                                entry.optionId === option.id
                                  ? { ...entry, quantity }
                                  : entry,
                              ),
                            )
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Surface>
        );
      })}

      {product.ingredients.length > 0 && (
        <section className="grid gap-3 border-t border-border pt-4">
          <h4 className="menu-text-primary font-bold">Ingredientes</h4>
          <p className="menu-text-muted text-xs leading-5">
            Ajusta la preparación a tu gusto.
          </p>
          <div className="divide-y divide-border/70">
            {product.ingredients.map((ingredient) =>
              ingredient.selectable ? (
                <Checkbox
                  key={ingredient.id}
                  className="min-h-12 py-2"
                  isSelected={isIngredientSelected(
                    value,
                    ingredient.id,
                    ingredient.includedByDefault,
                  )}
                  variant="secondary"
                  onChange={(selected: boolean) =>
                    setIngredient(ingredient, selected)
                  }
                >
                  <Checkbox.Content>
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                      <span className="menu-text-primary truncate font-medium">
                        {ingredient.name}
                      </span>
                      {ingredient.extraPriceUsdCents > 0 && (
                        <span className="menu-price shrink-0 text-xs font-bold">
                          +{formatUsd(ingredient.extraPriceUsdCents)}
                        </span>
                      )}
                    </span>
                  </Checkbox.Content>
                </Checkbox>
              ) : (
                <div
                  key={ingredient.id}
                  className="flex min-h-12 items-center justify-between gap-3 py-2 text-sm"
                >
                  <span className="menu-text-secondary min-w-0 truncate font-medium">
                    {ingredient.name}
                  </span>
                  <span className="menu-text-muted shrink-0 text-xs">
                    Incluido
                  </span>
                </div>
              ),
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function ComboFields({
  product,
  value,
  products,
  branchId,
  onChange,
}: {
  product: PublicProduct;
  value: CartProductConfiguration;
  products: ReadonlyMap<string, PublicProduct>;
  branchId: string;
  onChange(value: CartProductConfiguration): void;
}) {
  const setUnits = (componentId: string, units: ConfiguredProductInput[]) => {
    onChange({
      ...value,
      comboComponents: value.comboComponents.map((component) =>
        component.componentId === componentId
          ? { ...component, units }
          : component,
      ),
    });
  };

  return (
    <div className="menu-density-section grid gap-5">
      {product.comboComponents.map((component) => {
        const configured = value.comboComponents.find(
          (entry) => entry.componentId === component.id,
        );
        const units = configured?.units ?? [];
        const choices = component.products.map((choice) => {
          const choiceProduct = products.get(choice.productId);
          return {
            id: choice.productId,
            label: choiceProduct?.name ?? "Producto no disponible",
            description:
              choice.extraPriceUsdCents > 0
                ? `+${formatUsd(choice.extraPriceUsdCents)}`
                : "Incluido",
            disabled: !availableProduct(choiceProduct, branchId),
          };
        });
        return (
          <Surface
            key={component.id}
            className="menu-panel rounded-[var(--menu-radius)] border border-border p-4 sm:p-5"
            variant="default"
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="menu-text-primary font-black">
                  {component.name}
                </h3>
                <p className="menu-text-muted text-xs">
                  Configura {component.quantity}{" "}
                  {component.quantity === 1 ? "unidad" : "unidades"}.
                </p>
              </div>
              <span className="rounded-full bg-[var(--menu-accent)] px-3 py-1 text-xs font-bold text-[var(--menu-accent-foreground)]">
                {component.quantity}x
              </span>
            </div>
            <div className="grid gap-4">
              {Array.from({ length: component.quantity }, (_, index) => {
                const unit = units[index];
                const unitProduct = unit
                  ? products.get(unit.productId)
                  : undefined;
                return (
                  <Surface
                    key={`${component.id}-${index}`}
                    className="menu-panel-alt rounded-[var(--menu-radius)] border border-border p-4"
                    variant="secondary"
                  >
                    <MenuSelect
                      isDisabled={
                        component.selectionType === "fijo" &&
                        choices.filter((choice) => !choice.disabled).length ===
                          1
                      }
                      isRequired
                      items={choices}
                      label={`${component.name} ${index + 1}`}
                      placeholder="Elige un producto"
                      value={unit?.productId ?? ""}
                      onChange={(productId) => {
                        const nextProduct = products.get(productId);
                        if (!nextProduct) return;
                        const nextUnits = [...units];
                        nextUnits[index] = createProductConfiguration(
                          nextProduct,
                          branchId,
                        );
                        setUnits(component.id, nextUnits);
                      }}
                    />
                    {unit && unitProduct && (
                      <div className="mt-4 border-t border-border pt-4">
                        <ProductConfigurationFields
                          compact
                          branchId={branchId}
                          product={unitProduct}
                          value={unit}
                          onChange={(nextUnit) => {
                            const nextUnits = [...units];
                            nextUnits[index] = nextUnit;
                            setUnits(component.id, nextUnits);
                          }}
                        />
                      </div>
                    )}
                  </Surface>
                );
              })}
            </div>
          </Surface>
        );
      })}
    </div>
  );
}

export function ProductCustomizer({
  product,
  editLine,
  open,
  branchId,
  products,
  onClose,
  onAdded,
  mode = "drawer",
}: {
  product: PublicProduct | null;
  editLine: CartLine | null;
  open: boolean;
  branchId: string;
  products: ReadonlyMap<string, PublicProduct>;
  onClose(): void;
  onAdded?(product: PublicProduct): void;
  mode?: "drawer" | "inline";
}) {
  const { add } = useCart();
  const { tenant } = useTenant();
  const { show } = useToast();
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [configuration, setConfiguration] =
    useState<CartProductConfiguration | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const configuratorBodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !product) return;
    setQuantity(editLine?.quantity ?? 1);
    setActiveImage(0);
    setCurrentStep(0);
    setConfiguration(
      editLine
        ? cloneConfiguration(editLine.configuration)
        : createCartConfiguration(product, products, branchId),
    );
  }, [branchId, editLine, open, product, products]);

  const issue = useMemo(() => {
    if (!branchId)
      return "Selecciona una sucursal antes de agregar el producto.";
    return product && configuration
      ? configurationIssue(product, configuration, products, branchId)
      : null;
  }, [branchId, configuration, product, products]);
  const unitPrice = useMemo(
    () =>
      product && configuration
        ? configuredUnitPrice(product, configuration, products)
        : 0,
    [configuration, product, products],
  );

  const stepped = product ? needsSteps(product) : false;
  const steps = useProductSteps(product, configuration);
  const clampedStep = Math.min(currentStep, Math.max(0, steps.length - 1));
  const isLastStep = clampedStep >= steps.length - 1;

  const stepValid = useMemo(() => {
    if (!stepped || !configuration) return true;
    const step = steps[clampedStep];
    if (!step) return true;
    switch (step.type) {
      case "variant":
        return configuration.variantId !== null;
      case "option-group": {
        const group = product?.optionGroups.find((g) => g.id === step.groupId);
        if (!group) return true;
        const selections =
          configuration.groups.find((entry) => entry.groupId === group.id)
            ?.options ?? [];
        const selectionCount = selections.reduce(
          (sum, s) =>
            sum + (group.selectionType === "quantity" ? s.quantity : 1),
          0,
        );
        const min = Math.max(group.required ? 1 : 0, group.minSelections);
        return selectionCount >= min;
      }
      case "combo-component": {
        const component = product?.comboComponents.find(
          (c) => c.id === step.componentId,
        );
        if (!component) return true;
        const configured = configuration.comboComponents.find(
          (entry) => entry.componentId === component.id,
        );
        const units = configured?.units ?? [];
        return (
          component.quantity > 0 &&
          units.filter((u) => u.productId).length === component.quantity
        );
      }
      default:
        return true;
    }
  }, [clampedStep, configuration, product, stepped, steps]);

  const showConfiguratorHasMoreContent = useCallback(() => {
    const element = configuratorBodyRef.current;
    if (!element) return;
    const scrollableDistance = element.scrollHeight - element.clientHeight;
    if (scrollableDistance <= 24) return;
    const currentTop = element.scrollTop;
    const targetTop = Math.min(currentTop + 144, scrollableDistance);
    if (targetTop <= currentTop) return;
    element.scrollTo({
      top: targetTop,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const timeout = window.setTimeout(showConfiguratorHasMoreContent, 450);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    activeImage,
    clampedStep,
    open,
    product?.id,
    showConfiguratorHasMoreContent,
  ]);

  useEffect(() => {
    if (!open) return;
    configuratorBodyRef.current?.scrollTo({ top: 0 });
    const timeout = window.setTimeout(showConfiguratorHasMoreContent, 450);
    return () => window.clearTimeout(timeout);
  }, [clampedStep, open, product?.id, showConfiguratorHasMoreContent]);

  if (!product || !tenant || !configuration) return null;

  if (clampedStep !== currentStep) setCurrentStep(clampedStep);

  const submit = () => {
    if (!branchId) {
      show("Selecciona una sucursal antes de agregar productos.", "error");
      return;
    }
    if (issue) return;
    if (!add(product, quantity, configuration, unitPrice, editLine?.id)) {
      show(
        `El pedido admite como máximo ${MAX_CART_LINES} combinaciones de productos.`,
        "error",
      );
      return;
    }
    show(
      editLine
        ? `${product.name} actualizado.`
        : `${product.name} agregado al pedido.`,
      "success",
    );
    onAdded?.(product);
    onClose();
  };

  const setConfigPart = (next: ConfiguredProductInput) => {
    setConfiguration((prev) =>
      prev ? { ...next, comboComponents: prev.comboComponents } : prev,
    );
  };

  const stepContent = stepped
    ? (() => {
        const step = steps[clampedStep];
        if (!step) return null;
        switch (step.type) {
          case "variant":
            return (
              <VariantStep
                product={product}
                branchId={branchId}
                configuration={configuration}
                onChange={setConfigPart}
              />
            );
          case "option-group": {
            const group = product.optionGroups.find(
              (g) => g.id === step.groupId,
            );
            return group ? (
              <OptionGroupStep
                group={group}
                branchId={branchId}
                configuration={configuration}
                onChange={setConfigPart}
              />
            ) : null;
          }
          case "ingredients":
            return (
              <IngredientsStep
                product={product}
                configuration={configuration}
                onChange={setConfigPart}
              />
            );
          case "combo-component": {
            const component = product.comboComponents.find(
              (c) => c.id === step.componentId,
            );
            return component ? (
              <ComboComponentStep
                component={component}
                branchId={branchId}
                products={products}
                configuration={configuration}
                onChange={setConfiguration}
              />
            ) : null;
          }
          case "review":
            return (
              <ReviewStep product={product} configuration={configuration} />
            );
          default:
            return null;
        }
      })()
    : null;

  const footer = stepped ? (
    isLastStep ? (
      <div
        className="
        grid w-full min-w-0
        grid-cols-[auto_minmax(0,1fr)]
        gap-2
        sm:grid-cols-[auto_auto_minmax(0,1fr)]
        sm:items-center
        sm:gap-3
      "
      >
        {/* Quantity:
          mobile  -> full first row
          desktop -> second column
      */}
        <div className="order-1 col-span-2 min-w-0 sm:order-2 sm:col-span-1">
          <QuantityField
            compact
            label="Cantidad del producto"
            value={quantity}
            onChange={setQuantity}
          />
        </div>

        {/* Back:
          mobile  -> second row, left side
          desktop -> first column
      */}
        <Button
          className="order-2 min-h-12 sm:order-1"
          isDisabled={clampedStep === 0}
          variant="ghost"
          onPress={() => setCurrentStep((prev) => Math.max(0, prev - 1))}
        >
          Atrás
        </Button>

        {/* Primary action */}
        <Button
          fullWidth
          className="menu-critical-cta order-3 min-h-12 min-w-0"
          isDisabled={Boolean(issue)}
          size="lg"
          onPress={submit}
        >
          {editLine ? "Actualizar" : "Agregar"} ·{" "}
          {formatUsd(unitPrice * quantity)}
        </Button>
      </div>
    ) : (
      <div className="flex w-full min-w-0 items-center gap-2 sm:gap-3">
        <Button
          className="shrink-0"
          isDisabled={clampedStep === 0}
          variant="ghost"
          onPress={() => setCurrentStep((prev) => Math.max(0, prev - 1))}
        >
          Atrás
        </Button>

        <Button
          fullWidth
          className="menu-critical-cta min-w-0"
          isDisabled={!stepValid}
          size="lg"
          onPress={() =>
            setCurrentStep((prev) => Math.min(steps.length - 1, prev + 1))
          }
        >
          Siguiente
        </Button>
      </div>
    )
  ) : (
    <div
      className="
      grid w-full min-w-0
      grid-cols-1
      gap-2
      sm:grid-cols-[auto_minmax(0,1fr)]
      sm:items-center
      sm:gap-3
    "
    >
      <div className="min-w-0">
        <QuantityField
          compact
          label="Cantidad del producto"
          value={quantity}
          onChange={setQuantity}
        />
      </div>

      <Button
        fullWidth
        className="menu-critical-cta min-h-12 min-w-0"
        isDisabled={Boolean(issue)}
        size="lg"
        onPress={submit}
      >
        {editLine ? "Actualizar" : "Agregar"} ·{" "}
        {formatUsd(unitPrice * quantity)}
      </Button>
    </div>
  );

  const body = (
    <div className={`grid min-w-0 ${mode === "inline" ? "gap-4" : "gap-6"}`}>
      {product.images.length > 0 ? (
        <div className="grid gap-3">
          <img
            alt={`${product.name}, imagen ${activeImage + 1}`}
            className={`${mode === "inline" ? "h-36 sm:h-48" : "h-64 sm:h-80"} w-full rounded-[var(--menu-radius)] object-cover`}
            src={product.images[activeImage] ?? product.images[0]}
          />
          {product.images.length > 1 && (
            <div
              className="flex gap-2 overflow-x-auto pb-1"
              aria-label={`Imágenes de ${product.name}`}
            >
              {product.images.map((image, index) => (
                <Button
                  key={image}
                  isIconOnly
                  aria-label={`Ver imagen ${index + 1} de ${product.name}`}
                  aria-pressed={activeImage === index}
                  className={`h-16 w-20 shrink-0 overflow-hidden rounded-[var(--menu-radius)] border-2 p-0 ${activeImage === index ? "border-accent" : "border-border"}`}
                  variant="ghost"
                  onPress={() => setActiveImage(index)}
                >
                  <img
                    alt=""
                    className="h-full w-full object-cover"
                    src={image}
                  />
                </Button>
              ))}
            </div>
          )}
        </div>
      ) : mode === "drawer" ? (
        <Surface
          className="menu-panel-alt grid h-44 place-items-center rounded-[var(--menu-radius)] text-5xl"
          variant="tertiary"
          aria-label="Producto sin imagen"
        >
          ○
        </Surface>
      ) : null}

      <div className="grid gap-1">
        {mode === "drawer" ? (
          <p className="menu-text-secondary text-sm leading-6">
            {product.description ||
              "Preparado al momento con ingredientes frescos."}
          </p>
        ) : null}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <strong className="menu-price text-2xl font-black">
            {formatUsd(unitPrice)}
          </strong>
          <span className="menu-text-muted text-sm font-bold">
            {formatLocal(
              unitPrice,
              tenant.exchangeRate,
              tenant.localCurrency,
              tenant.currencyDecimals,
              tenant.currencySymbol,
            )}
          </span>
        </div>
      </div>

      {issue && <ConfigurationAlert issue={issue} />}

      {stepped ? (
        <>
          <ProductStepper steps={steps} currentStepIndex={clampedStep} />
          {stepContent}
        </>
      ) : (
        <>
          <ProductConfigurationFields
            branchId={branchId}
            product={product}
            value={configuration}
            onChange={(next) =>
              setConfiguration({
                ...next,
                comboComponents: configuration.comboComponents,
              })
            }
          />
          {product.type === "combo" && (
            <ComboFields
              branchId={branchId}
              product={product}
              products={products}
              value={configuration}
              onChange={setConfiguration}
            />
          )}
        </>
      )}
    </div>
  );

  if (mode === "inline") {
    return (
      <div className="grid max-h-[70vh] w-full min-w-0 max-w-none gap-4 overflow-y-auto px-0 pb-0 pt-1">
        {body}
        <div className="sticky bottom-0 border-t border-border bg-[var(--menu-surface)]/95 py-3 backdrop-blur">
          {footer}
        </div>
      </div>
    );
  }

  return (
    <ResponsiveDrawer
      open={open}
      panel="configurator"
      title={editLine ? `Editar ${product.name}` : product.name}
      wide
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      footer={footer}
      bodyRef={configuratorBodyRef}
    >
      {body}
    </ResponsiveDrawer>
  );
}
